/**
 * Managed Agents — one agent-type card: the activation toggle, the schedule
 * editor and "Run now". Extracted verbatim from ClientAgentsPage so the Skills
 * per-agent config sub-page (and any future host) can render it unchanged. The
 * `client-agents__*` styles it uses live in `../client-reports.scss`, kept
 * imported here so the global SCSS bundle carries them.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import MainSquareButton from '../../../ui/buttons/MainSquareButton';
import ToggleSwitch from '../../../ui/buttons/ToggleSwitch';
import SchedulePicker from './SchedulePicker';
import {
  agentRunBlocker,
  agentRunStatusLabel,
  agentStateChip,
  browserTimezone,
  expectedPeriodText,
  formatCentsEUR,
  formatRunMoment,
  isRunInFlight,
  isValidCronShape,
  nextRunPhrase,
  runBlockerMessage,
  type Translate,
} from './agent-helpers';
import type {
  AgentSchedule,
  AgentView,
  RunView,
  TypeView,
} from '../../../lib/api/client-reports/client-agents-api';
import '../client-reports.scss';

/** Friendly names for the deliverables an agent type produces. */
function outputLabel(output: string, t: Translate): string {
  switch (output) {
    case 'html':
      return t('AgentOutputHtml', 'HTML report');
    case 'pptx':
      return t('AgentOutputPptx', 'PPTX deck');
    case 'xlsx':
      return t('AgentOutputXlsx', 'XLSX workbook');
    default:
      return output.toUpperCase();
  }
}

function outputsLabel(outputs: string[], t: Translate): string {
  if (!Array.isArray(outputs) || outputs.length === 0) return '—';
  return outputs.map((o) => outputLabel(o, t)).join(' + ');
}

export type BusyAction = 'activate' | 'deactivate' | 'schedule' | 'run';

interface ScheduleDraft {
  cron: string;
  timezone: string;
}

/**
 * The editor mirrors the SAVED agent schedule; an unactivated type previews
 * the registry default it would be created with (timezone falling back to the
 * browser's IANA zone when the registry has none).
 */
function scheduleDraftFrom(
  agent: AgentView | null,
  type: TypeView
): ScheduleDraft {
  return {
    cron: agent?.schedule?.cron ?? type.default_schedule?.cron ?? '',
    timezone:
      agent?.schedule?.timezone ??
      type.default_schedule?.timezone ??
      browserTimezone(),
  };
}

interface AgentCardProps {
  type: TypeView;
  agent: AgentView | null;
  /** Runs for THIS agent type only, newest first. */
  runs: RunView[];
  canManage: boolean;
  busy: BusyAction | null;
  onToggle: (type: TypeView, agent: AgentView | null, checked: boolean) => void;
  onSaveSchedule: (
    type: TypeView,
    agent: AgentView,
    schedule: AgentSchedule
  ) => void;
  onRunNow: (type: TypeView, agent: AgentView) => void;
}

const AgentCard = ({
  type,
  agent,
  runs,
  canManage,
  busy,
  onToggle,
  onSaveSchedule,
  onRunNow,
}: AgentCardProps) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<ScheduleDraft>(
    scheduleDraftFrom(agent, type)
  );

  // Re-anchor the editor whenever the SAVED values change — keyed on the
  // values, not object identities, because the 10s runs-poll rebuilds the
  // agent/type objects and must never wipe an in-progress edit.
  const savedCron = agent?.schedule?.cron ?? type.default_schedule?.cron ?? '';
  const savedTimezone =
    agent?.schedule?.timezone ?? type.default_schedule?.timezone ?? '';
  useEffect(() => {
    setDraft({
      cron: savedCron,
      timezone: savedTimezone || browserTimezone(),
    });
  }, [savedCron, savedTimezone]);

  const chip = agentStateChip(agent, t);
  const cronValid = isValidCronShape(draft.cron);
  const timezoneValid = draft.timezone.trim() !== '';

  const base = scheduleDraftFrom(agent, type);
  const isDirty = base.cron !== draft.cron || base.timezone !== draft.timezone;

  const canSaveSchedule =
    canManage && !!agent && isDirty && cronValid && timezoneValid && !busy;

  /** Why the schedule Save is greyed out, as the button's tooltip. */
  const saveBlockedReason = (): string => {
    if (canSaveSchedule) return '';
    if (!agent) {
      return t('AgentCardSwitchOnFirst', 'Switch {{agent}} on first.', {
        agent: type.label,
      });
    }
    if (!cronValid) {
      return t(
        'AgentCardPickScheduleFirst',
        'Pick how often, a day and a time first.'
      );
    }
    if (!timezoneValid) {
      return t('AgentCardTimezoneRequired', 'A timezone is required.');
    }
    if (!isDirty) return t('AgentCardNoChanges', 'No changes to save.');
    return '';
  };

  const blocker = agentRunBlocker(agent, runs);
  const blockedReason = runBlockerMessage(
    blocker,
    t('AgentTheNamedAgent', 'the {{agent}}', { agent: type.label }),
    t
  );

  const inFlightRun = runs.find((r) => isRunInFlight(r.status));
  const runNowPeriod = expectedPeriodText(agent?.run_now_period, t);
  const lastLine = agent?.last_run_at
    ? formatRunMoment(agent.last_run_at, undefined, t)
    : t('AgentCardNever', 'Never');
  const nextLine = agent?.is_active
    ? nextRunPhrase(agent.next_run_at, !agent.last_run_at, undefined, t)
    : '';

  return (
    <section className='client-agents__card'>
      <div className='client-agents__card-head'>
        <div className='client-agents__card-id'>
          <h3 className='client-agents__card-title'>{type.label}</h3>
          <span
            className={`client-agents__chip client-agents__chip--${chip.tone}`}
          >
            {chip.label}
          </span>
          {inFlightRun && (
            <span className='client-agents__chip client-agents__chip--warning client-agents__chip--pulse'>
              {agentRunStatusLabel(inFlightRun.status, t)}
            </span>
          )}
        </div>
        {canManage && (
          <ToggleSwitch
            id={`agent-active-${type.agent_type}`}
            isOn={!!agent && agent.is_active}
            disabled={busy === 'activate' || busy === 'deactivate'}
            message={t('AgentCardWorking', 'Working…')}
            onClick={(checked: boolean) => onToggle(type, agent, checked)}
          />
        )}
      </div>

      <p className='client-agents__card-hint'>
        {t(
          'AgentCardDelivers',
          'Delivers {{outputs}} on a schedule, straight into your report review queue.',
          { outputs: outputsLabel(agent?.outputs ?? type.outputs, t) }
        )}
      </p>

      {agent?.needs_attention && (
        <div className='client-agents__banner' role='alert'>
          <span className='client-agents__banner-title'>
            {t('AgentCardNeedsAttention', 'This agent needs attention')}
          </span>
          <span className='client-agents__banner-body'>
            {agent.needs_attention.reason ||
              t(
                'AgentCardNeedsAttentionBody',
                'Something went wrong with the last runs — AdUp has been notified.'
              )}
          </span>
        </div>
      )}

      <div className='client-agents__facts'>
        <div className='client-agents__fact'>
          <span className='client-agents__fact-label'>
            {t('AgentCardPerRunBudget', 'Per-run budget')}
          </span>
          <span className='client-agents__fact-value'>
            {formatCentsEUR(
              agent ? agent.effective_budget_cents : type.default_budget_cents
            )}
          </span>
        </div>
        <div className='client-agents__fact'>
          <span className='client-agents__fact-label'>
            {t('AgentCardNextRun', 'Next run')}
          </span>
          <span className='client-agents__fact-value'>
            {agent?.is_active
              ? formatRunMoment(agent.next_run_at, undefined, t) ||
                t('AgentCardBeingScheduled', 'Being scheduled…')
              : '—'}
          </span>
        </div>
        <div className='client-agents__fact'>
          <span className='client-agents__fact-label'>
            {t('AgentCardLastRun', 'Last run')}
          </span>
          <span className='client-agents__fact-value'>{lastLine}</span>
        </div>
      </div>

      {agent && agent.is_active && runs.length === 0 && (
        <p className='client-agents__firstline'>
          {nextLine
            ? t('AgentNoReportsYetNext', 'No reports yet — {{next}}.', {
                next: nextLine,
              })
            : t(
                'AgentNoReportsYetSoon',
                'No reports yet — the first run date appears here shortly.'
              )}
        </p>
      )}

      {/* ---- schedule (Agents v3: a picker in words, cron stays on the wire) ---- */}
      <div className='client-agents__schedule'>
        <h4 className='client-agents__settings-title'>
          {t('AgentCardSchedule', 'Schedule')}
        </h4>
        <SchedulePicker
          cron={draft.cron}
          timezone={draft.timezone}
          allowedCadences={type.allowed_cadences}
          disabled={!canManage || !agent}
          idPrefix={`agent-schedule-${type.agent_type}`}
          onChange={(cron) => setDraft((d) => ({ ...d, cron }))}
        />
        {!cronValid && (
          <span className='client-agents__validation'>
            {t(
              'AgentCardScheduleInvalid',
              "This schedule can't be saved — pick how often, a day and a time above."
            )}
          </span>
        )}

        {!agent && (
          <p className='client-agents__muted'>
            {type.remembered_schedule
              ? t(
                  'AgentCardRememberedSchedule',
                  'This is the schedule {{agent}} had last time. Switching it on uses it again — you can change it afterwards.',
                  { agent: type.label }
                )
              : t(
                  'AgentCardDefaultSchedule',
                  'Switching {{agent}} on activates it with this schedule — you can change it afterwards.',
                  { agent: type.label }
                )}
          </p>
        )}

        {canManage && agent && (
          <div className='client-agents__schedule-actions'>
            <MainSquareButton
              type='button'
              value={
                busy === 'schedule'
                  ? t('AgentCardSaving', 'Saving…')
                  : t('AgentCardSaveSchedule', 'Save schedule')
              }
              buttonType='light'
              normal
              loading={busy === 'schedule'}
              disabled={!canSaveSchedule}
              message={saveBlockedReason()}
              onClick={() =>
                onSaveSchedule(type, agent, {
                  cron: draft.cron.trim(),
                  timezone: draft.timezone.trim(),
                })
              }
            />
          </div>
        )}
      </div>

      {/* ---- run now ---- */}
      {canManage && (
        <div className='client-agents__run-row'>
          <MainSquareButton
            type='button'
            value={
              busy === 'run'
                ? t('AgentCardStarting', 'Starting…')
                : t('AgentCardRunNow', 'Run now')
            }
            buttonType='success'
            normal
            loading={busy === 'run'}
            disabled={busy === 'run' || blocker !== null}
            message={blockedReason}
            onClick={() => agent && onRunNow(type, agent)}
          />
          {blocker ? (
            // A tooltip alone is undiscoverable on a disabled button — and
            // this is exactly the state a fresh brand starts in.
            <p className='client-agents__validation'>{blockedReason}</p>
          ) : (
            <p className='client-agents__hint'>
              {runNowPeriod
                ? t(
                    'AgentCardRunNowHintPeriod',
                    '"Run now" reports {{period}} straight away: the last completed period of the saved schedule. For another period, save a different schedule first.',
                    { period: runNowPeriod }
                  )
                : t(
                    'AgentCardRunNowHint',
                    '"Run now" produces the report for the last completed period straight away.'
                  )}
            </p>
          )}
        </div>
      )}
    </section>
  );
};

export default AgentCard;
